import { z } from 'zod';
import { LedgerEntrySetSortDirectionSchema } from '../ledger.js';
import { BooleanQueryParamSchema, CalendarDateOnlySchema, PaginationQuerySchema } from './common.js';
// =============================================================================
// Cashbook (administered entity)
// =============================================================================
export const CashbookSortBySchema = z.enum(['date', 'debit', 'credit']);
/**
 * Query for one page of the cashbook report. `bankAccountId` (a `BankAccount`
 * id, resolved server-side to the ledger's matching `BANK`-role ledger
 * account) narrows the report to a single cash account and switches
 * `CashbookReport.mode` to `'SINGLE_ACCOUNT'`; omitted, the report covers
 * every `BANK`-role account on the ledger in `'LEDGER'` mode. No span cap -
 * the report period is fixed by the appointment, not a UI convenience window.
 *
 * `from` is an optional lower bound. Omitted, the read is unbounded at the
 * start (All time): opening balance is conversion legs only, matching VAT
 * control and the general ledger. A concrete `from` is a brought-forward
 * window, not a hidden far-past floor.
 *
 * `ledgerAccountId` narrows the listed movements to the ones classified from
 * that ledger account (the paged set's own legs, or its imported line's
 * allocation-set legs) - an extract, not a different total: opening/closing
 * balances and the period totals are always the unfiltered s 92 figures. A
 * ledger account outside this ledger is rejected with 400, and so is one
 * whose role can never be a classification (the cash-boundary and reconciling
 * roles, plus VAT Control) - a movement's Account column never shows one of
 * these, so filtering by one would silently return an incoherent page.
 *
 * `unallocatedOnly` narrows the listed movements to the ones with NO
 * allocation yet - the residual a caller chases, as opposed to "what was
 * allocated to Bank Suspense" (meaningless, since every imported line posts
 * through Bank Suspense whether it has been allocated or not). Mutually
 * exclusive with `ledgerAccountId`: the two are one control on the Bill
 * cashbook screen, and supplying both is rejected with 400.
 *
 * `search` matches stored bank narrations, allocation descriptions and journal
 * descriptions case-insensitively, including those of a reversal's original.
 * A matching source description need not be the one displayed on the row.
 *
 * `sortBy` defaults to Date for existing clients. Debit and Credit order entry
 * sets by the sum of their in-scope BANK legs on that side, with blanks last
 * in both directions and chronological ties. Date descending exactly reverses
 * the ascending `(accountingDate, createdAt, id)` tuple. Balances and vouchers
 * remain chronological leg facts under every sort. Direction defaults to asc.
 */
export const CashbookQuerySchema = PaginationQuerySchema.extend({
    from: CalendarDateOnlySchema.optional(),
    to: CalendarDateOnlySchema,
    bankAccountId: z.uuid().optional(),
    ledgerAccountId: z.uuid().optional(),
    unallocatedOnly: BooleanQueryParamSchema.optional(),
    search: z.string().max(200).optional(),
    sortBy: CashbookSortBySchema.optional().prefault('date'),
    sortDirection: LedgerEntrySetSortDirectionSchema.optional().prefault('asc'),
});
/**
 * Query for the cashbook zip export. The same window and bank filter as
 * {@link CashbookQuerySchema}, without `cursor` / `limit`: the exporter walks
 * `getCashbookReport` itself at a fixed page size.
 */
export const CashbookExportQuerySchema = z.object({
    from: CalendarDateOnlySchema.optional(),
    to: CalendarDateOnlySchema,
    bankAccountId: z.uuid().optional(),
});
export const CashbookModeSchema = z.enum(['LEDGER', 'SINGLE_ACCOUNT']);
const CashbookLedgerAccountSchema = z.object({
    id: z.uuid(),
    code: z.string(),
    name: z.string(),
});
export const CashbookCashAccountSchema = z.object({
    ledgerAccountId: z.uuid(),
    code: z.string(),
    name: z.string(),
    bankAccountId: z.uuid().nullable(),
});
/**
 * The client or supplier a classification leg is tagged with, alongside its
 * resolved `partyName`; at most one is set. `.optional()` only so a new bundle
 * safe-parses an old server's response during a deploy window; the backend
 * always sets both.
 * TODO: make both required (`.nullable()` only) once the release adding them is live.
 */
const CashbookPartyIdsShape = {
    clientId: z.uuid().nullable().optional(),
    supplierId: z.uuid().nullable().optional(),
};
export const CashbookAllocationLegSchema = z.object({
    account: CashbookLedgerAccountSchema,
    amountMinorUnits: z.number().int(),
    partyName: z.string().nullable(),
    ...CashbookPartyIdsShape,
});
/**
 * How a movement's cash leg was classified, derived from its allocation entry
 * set's non-cash, non-VAT legs.
 */
export const CashbookAllocationSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('ACCOUNT'),
        account: CashbookLedgerAccountSchema,
        partyName: z.string().nullable(),
        ...CashbookPartyIdsShape,
    }),
    z.object({ kind: z.literal('TRANSFER') }),
    z.object({ kind: z.literal('UNALLOCATED') }),
    z.object({
        kind: z.literal('SPLIT'),
        legs: z.array(CashbookAllocationLegSchema),
    }),
]);
/**
 * Label for a movement's classification, used when there is no per-leg
 * breakdown to render (everything except `SPLIT`, whose legs render as
 * extra particulars rows instead).
 */
export function cashbookAllocationLabel(allocation) {
    switch (allocation.kind) {
        case 'ACCOUNT':
            return cashbookPartyLabel(allocation.account.name, allocation.partyName);
        case 'SPLIT':
            return 'Split';
        case 'TRANSFER':
            return 'Transfer between accounts';
        case 'UNALLOCATED':
            return 'Unallocated';
    }
}
/** Between an account name and its counterparty: a spaced en dash (U+2013). */
export const CASHBOOK_PARTY_SEPARATOR = ' – ';
/**
 * Appends a counterparty name to an account name when one is known. The
 * on-screen cashbook renders the same parts with the party linked, joined by
 * `CASHBOOK_PARTY_SEPARATOR`.
 */
export function cashbookPartyLabel(accountName, partyName) {
    return partyName ? `${accountName}${CASHBOOK_PARTY_SEPARATOR}${partyName}` : accountName;
}
/**
 * Received/Disbursed pair for one SPLIT leg, from the leg's own signed
 * `amountMinorUnits` - never from the parent movement's column. A
 * classification leg is a contra entry to the movement's BANK leg (debit
 * positive), so its sign is the opposite of the direction it represents: a
 * credit leg (negative) is the classification side of money received, a
 * debit leg (positive) the classification side of money disbursed.
 */
export function cashbookSplitLegAmounts(amountMinorUnits) {
    if (amountMinorUnits < 0)
        return { receivedMinorUnits: -amountMinorUnits, disbursedMinorUnits: null };
    if (amountMinorUnits > 0)
        return { receivedMinorUnits: null, disbursedMinorUnits: amountMinorUnits };
    return { receivedMinorUnits: null, disbursedMinorUnits: null };
}
/**
 * One cashbook row: a single BANK-role ledger leg. Set-level figures
 * (`vatMinorUnits`, `voucherCount`) are carried on the first in-scope leg of
 * the set only; identity pointers repeat on every leg.
 *
 * `isReversal` is a boolean until a second movement kind exists. A
 * `{ type: 'REVERSAL' } | null` discriminant is reserved for that day.
 */
export const CashbookMovementSchema = z.object({
    ledgerEntryId: z.uuid(),
    entrySetId: z.uuid(),
    bankStatementLineId: z.uuid().nullable(),
    allocationEntrySetId: z.uuid().nullable(),
    accountingDate: CalendarDateOnlySchema,
    description: z.string(),
    bankLedgerAccount: CashbookLedgerAccountSchema,
    receivedMinorUnits: z.number().int().min(1).nullable(),
    disbursedMinorUnits: z.number().int().min(1).nullable(),
    vatMinorUnits: z.number().int().nonnegative().nullable(),
    balanceMinorUnits: z.number().int(),
    voucherNumber: z.number().int().min(1),
    voucherCount: z.number().int().nonnegative(),
    allocation: CashbookAllocationSchema,
    isReversal: z.boolean(),
    // Supplier invoices tagged on the classification set's Accounts Payable
    // legs or on any payment settlement journal reconciled to the line -
    // the third voucher parent alongside the bank statement line and the
    // allocation journal. Distinct ids, in leg order, repeated on every
    // leg of the set (an identity pointer, like `allocationEntrySetId`); empty
    // when none is tagged. `.optional()` only so a new bundle safe-parses an
    // old server's response during a deploy window (the `coveredInvoiceIds`
    // precedent, `ClientStatementSchema`); the backend always sets it and
    // callers read `movement.supplierInvoiceIds ?? []`.
    supplierInvoiceIds: z.array(z.uuid()).optional(),
});
export const CashbookTransferMemoSchema = z.object({
    receivedMinorUnits: z.number().int().nonnegative(),
    disbursedMinorUnits: z.number().int().nonnegative(),
});
export const CashbookStatementTieOutSchema = z.object({
    statementId: z.uuid(),
    statementClosingBalanceMinorUnits: z.number().int(),
    differenceMinorUnits: z.number().int(),
});
const CashbookEntitySchema = z.object({
    accountId: z.uuid(),
    name: z.string(),
}).strict();
/**
 * The FIRST page of the cashbook: one page of movements plus every
 * whole-period figure the report's header and footer carry.
 *
 * Only the first page carries them. They are identical on every page of one
 * report, and the two that cost the most - the period receipts and payments -
 * scan the whole requested window, which on an estate's full history is its
 * entire life. Recomputing them per page multiplied that by however far the
 * reader had scrolled, for figures the client reads once.
 *
 * They are absent from a continuation page rather than nullable, which is the
 * whole reason for the discriminated union: a nullable money field cannot tell
 * a later reader "this is page two" apart from "the aggregate failed", and the
 * tempting repair for a nullable total is `?? 0` - reporting nil receipts on a
 * record filed with the Master. `kind` makes the wrong question impossible to
 * ask instead of merely wrong to answer.
 *
 * `from` is absent when the query omitted it (All time). Echoing a fabricated
 * start date would print a floor that is not an accounting date.
 */
export const CashbookReportSchema = z.object({
    kind: z.literal('REPORT'),
    ledgerId: z.uuid(),
    from: CalendarDateOnlySchema.optional(),
    to: CalendarDateOnlySchema,
    currency: z.string().length(3),
    entity: CashbookEntitySchema,
    mode: CashbookModeSchema,
    bankAccountId: z.uuid().nullable(),
    cashAccounts: z.array(CashbookCashAccountSchema),
    openingBalanceMinorUnits: z.number().int(),
    closingBalanceMinorUnits: z.number().int(),
    totalReceivedMinorUnits: z.number().int().nonnegative(),
    totalDisbursedMinorUnits: z.number().int().nonnegative(),
    transferMemo: CashbookTransferMemoSchema.nullable(),
    statementTieOut: CashbookStatementTieOutSchema.nullable(),
    movements: z.array(CashbookMovementSchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
});
/**
 * A CONTINUATION page: movements and nothing else.
 *
 * The running balance and the voucher number a printed sheet needs are already
 * per-movement (`balanceMinorUnits`, `voucherNumber`), computed server-side
 * from a seed the client never sees, so a later page needs no header figure to
 * render its own rows correctly.
 */
export const CashbookMovementsPageSchema = z.object({
    kind: z.literal('MOVEMENTS'),
    movements: z.array(CashbookMovementSchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
});
/**
 * What the cashbook endpoint returns: the full report for the first page (no
 * cursor), movements alone for every page after it.
 *
 * `kind` names WHAT the payload is rather than which page index produced it. A
 * reader asking "is this the report" is asking the question the union exists to
 * answer; one asking "is this page one" would be re-deriving it from the cursor
 * at the call site, which is exactly the coupling this shape removes.
 */
export const CashbookPageSchema = z.discriminatedUnion('kind', [
    CashbookReportSchema,
    CashbookMovementsPageSchema,
]);
